/**
 * Google Analytics 4 (GA4) & Google Tag Manager (GTM) Tracking Utility
 * Specially tailored for Matrimonial Platform (Vivashri)
 * 
 * Standard GA4 Ecommerce + Recommended Events + Matrimony Custom KPIs
 */

/**
 * Core event tracking engine (Dual sync to GA4 gtag & GTM dataLayer)
 */
export const trackEvent = (eventName, eventParams = {}) => {
  try {
    if (typeof window === "undefined") return;

    // Ensure dataLayer exists for GTM
    window.dataLayer = window.dataLayer || [];

    const payload = {
      event: eventName,
      ...eventParams,
      platform: "Matrimony Web App",
      timestamp: new Date().toISOString(),
    };

    // 1. Push to Google Tag Manager
    window.dataLayer.push(payload);

    // 2. Push to Google Analytics 4 (gtag.js)
    if (typeof window.gtag === "function") {
      window.gtag("event", eventName, eventParams);
    }

    // Dev debug logs
    // if (process.env.NODE_ENV === "development") {
    //   console.log(`💍 [Matrimony Analytics]: ${eventName}`, eventParams);
    // }
  } catch (error) {
    console.error("Matrimony Analytics error:", error);
  }
};

/* ==========================================================================
   1. USER ONBOARDING & AUTHENTICATION (Conversion Funnel)
   ========================================================================== */

/**
 * User starts creating matrimony profile / registration
 */
export const trackFormStart = (stepName = "Registration Step 1", stepNumber = 1) => {
  trackEvent("form_start", {
    form_name: "Matrimony Profile Creation",
    step_name: stepName,
    step_number: stepNumber,
  });
};

/**
 * User completes registration / New profile created
 */
export const trackSignUp = (method = "Mobile OTP", userId = null, profileFor = "Self") => {
  trackEvent("sign_up", {
    method: method,
    profile_for: profileFor,
    ...(userId && { user_id: String(userId) }),
  });
};

/**
 * Member logged in successfully
 */
export const trackLogin = (method = "Mobile OTP", userId = null) => {
  trackEvent("login", {
    method: method,
    ...(userId && { user_id: String(userId) }),
  });
};

/**
 * Track Authentication Success (GTM Event for New vs Existing User)
 */
export const trackAuthSuccess = (userType = "existing") => {
  try {
    if (typeof window === "undefined") return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "vivashri_auth_success",
      user_type: userType === "new" ? "new" : "existing",
    });

    // if (process.env.NODE_ENV === "development") {
    //   console.log(`💍 [Matrimony Analytics]: vivashri_auth_success`, { user_type: userType });
    // }
  } catch (error) {
    console.error("Matrimony Analytics auth_success error:", error);
  }
};

/**
 * User completes a specific profile step (Basic, Religion, Education, Family, Partner Preferences)
 */
export const trackProfileStepComplete = (stepName, stepNumber, userId = null) => {
  trackEvent("profile_step_complete", {
    step_name: stepName,
    step_number: stepNumber,
    ...(userId && { user_id: String(userId) }),
  });
};

/**
 * Member uploads profile photos (Crucial quality metric)
 */
export const trackPhotoUpload = (photoCount = 1) => {
  trackEvent("photo_upload", {
    photo_count: photoCount,
  });
};

/**
 * Aadhaar / Identity Verification completed
 */
export const trackAadhaarVerify = (status = "Success") => {
  trackEvent("id_verification", {
    verification_status: status,
    verification_type: "Aadhaar",
  });
};


/* ==========================================================================
   2. MATCHMAKING & DISCOVERY (Engagement KPIs)
   ========================================================================== */

/**
 * Member searches for bride/groom profiles
 */
export const trackSearch = (searchFilters = {}) => {
  trackEvent("search", {
    search_type: searchFilters.type || "Basic Search",
    religion: searchFilters.religion || "All",
    caste: searchFilters.caste || "All",
    state: searchFilters.state || "All",
    age_range: searchFilters.age_range || "All",
    search_term: JSON.stringify(searchFilters),
  });
};

/**
 * Member views another user's matrimony profile
 */
export const trackProfileView = (profileId, profileName = "", gender = "", religion = "", caste = "") => {
  trackEvent("view_item", {
    content_type: "Matrimony Profile",
    item_id: String(profileId),
    item_name: profileName || `Profile_${profileId}`,
    gender: gender,
    religion: religion,
    caste: caste,
  });
};

/**
 * Member sends Interest / Connect request to a match (GA4 add_to_wishlist)
 */
export const trackSendInterest = (targetProfileId, targetName = "") => {
  trackEvent("add_to_wishlist", {
    action_type: "Send Interest",
    items: [
      {
        item_id: String(targetProfileId),
        item_name: targetName || `Profile_${targetProfileId}`,
        item_category: "Interest Sent",
      },
    ],
  });
};

/**
 * Member accepts an incoming Interest request
 */
export const trackAcceptInterest = (targetProfileId) => {
  trackEvent("accept_interest", {
    target_profile_id: String(targetProfileId),
  });
};

/**
 * Member shortlists / bookmarks a profile
 */
export const trackShortlistProfile = (targetProfileId, targetName = "") => {
  trackEvent("add_to_wishlist", {
    action_type: "Shortlist Profile",
    items: [
      {
        item_id: String(targetProfileId),
        item_name: targetName || `Profile_${targetProfileId}`,
        item_category: "Shortlisted",
      },
    ],
  });
};

/**
 * Member clicks to View / Unlock Contact Details (Primary Matrimony Lead KPI)
 */
export const trackViewContact = (targetProfileId, userPlan = "Free") => {
  trackEvent("generate_lead", {
    lead_type: "View Contact Number",
    target_profile_id: String(targetProfileId),
    user_plan: userPlan,
  });
};

/**
 * Member initiates real-time chat with a match
 */
export const trackChatStart = (targetProfileId) => {
  trackEvent("chat_initiate", {
    target_profile_id: String(targetProfileId),
  });
};

/**
 * Member shares a matrimony profile via WhatsApp / Copy Link / Social
 */
export const trackShare = (profileId = "", platform = "WhatsApp") => {
  trackEvent("share", {
    content_type: "Matrimony Profile",
    item_id: String(profileId),
    method: platform,
  });
};


/* ==========================================================================
   3. MONETIZATION & MEMBERSHIP UPGRADES (Revenue Funnel)
   ========================================================================== */

/**
 * Member views membership pricing page
 */
export const trackViewPlans = () => {
  trackEvent("view_promotion", {
    promotion_name: "Membership Packages",
  });
};

/**
 * Member selects a plan & clicks "Buy Now / Upgrade" (GA4 begin_checkout)
 */
export const trackBeginCheckout = (planName, price, planId = null) => {
  trackEvent("begin_checkout", {
    currency: "INR",
    value: Number(price) || 0,
    items: [
      {
        item_id: String(planId || planName),
        item_name: planName,
        price: Number(price) || 0,
        item_category: "Membership Plan",
        quantity: 1,
      },
    ],
  });
};

/**
 * Add To Cart (Plan Selection)
 */
export const trackAddToCart = (planName, price, planId = null) => {
  trackEvent("add_to_cart", {
    currency: "INR",
    value: Number(price) || 0,
    items: [
      {
        item_id: String(planId || planName),
        item_name: planName,
        price: Number(price) || 0,
        item_category: "Membership Plan",
        quantity: 1,
      },
    ],
  });
};

/**
 * Successful Plan Purchase / Membership Activation (GA4 purchase)
 */
export const trackPurchase = (transactionId, planName, amount, currency = "INR") => {
  trackEvent("purchase", {
    transaction_id: transactionId || `TXN_${Date.now()}`,
    value: Number(amount) || 0,
    currency: currency,
    items: [
      {
        item_name: planName,
        item_category: "Membership Plan",
        price: Number(amount) || 0,
        quantity: 1,
      },
    ],
  });
};

/**
 * Generic CTA click tracking
 */
export const trackClick = (buttonText, section = "General") => {
  trackEvent("click", {
    link_text: buttonText,
    section: section,
  });
};

/**
 * Default export
 */
export default {
  trackEvent,
  trackFormStart,
  trackSignUp,
  trackLogin,
  trackAuthSuccess,
  trackProfileStepComplete,
  trackPhotoUpload,
  trackAadhaarVerify,
  trackSearch,
  trackProfileView,
  trackSendInterest,
  trackAcceptInterest,
  trackShortlistProfile,
  trackViewContact,
  trackChatStart,
  trackShare,
  trackViewPlans,
  trackBeginCheckout,
  trackAddToCart,
  trackPurchase,
  trackClick,
};
